import { currentUserId, currentUserQuery, suiteRoot } from "./auth";

export type SignFolder = { id: number; name: string; count: number };
export type SignDoc = { id: number; title: string; status: string; folder: string; folder_id: number; sealed: boolean };

async function signGet(path: string): Promise<Record<string, unknown>> {
  const headers = new Headers();
  const userId = currentUserId();
  if (userId) headers.set("X-User-Id", userId);
  const res = await fetch(`${suiteRoot()}/sign/api/${path}`, { credentials: "include", cache: "no-store", headers });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 403) throw new Error("La tuile Signature n’est pas ouverte pour ce compte.");
  if (!res.ok || data.ok === false) throw new Error(String(data.message || "Signature ne répond pas."));
  return data;
}

export async function listSignFolders(): Promise<SignFolder[]> {
  const data = await signGet("folders.php");
  const rows = Array.isArray(data.folders) ? data.folders : [];
  return rows.flatMap((row) => {
    const item = row as { id?: number; name?: string; doc_count?: number };
    const id = Number(item.id) || 0;
    if (id < 1) return [];
    return [{ id, name: item.name || "Dossier", count: Number(item.doc_count) || 0 }];
  });
}

export function signFileUrl(doc: Pick<SignDoc, "id" | "status" | "sealed">): string {
  const which = doc.status === "signed" ? "pack" : doc.sealed ? "sealed" : "original";
  const extra = currentUserQuery().replace(/^\?/, "");
  return `${suiteRoot()}/sign/api/document-file.php?id=${doc.id}&which=${which}${extra ? `&${extra}` : ""}`;
}

export async function listSignDocs(folderId: number): Promise<SignDoc[]> {
  const query = folderId > 0 ? `?folder_id=${folderId}` : "";
  const data = await signGet(`documents.php${query}`);
  const rows = Array.isArray(data.documents) ? data.documents : [];
  return rows.map((row) => {
    const item = row as { id?: number; title?: string; status?: string; folder_name?: string; folder_id?: number; has_sealed?: boolean };
    return {
      id: Number(item.id) || 0,
      title: item.title || "Contrat",
      status: item.status || "draft",
      folder: item.folder_name || "",
      folder_id: Number(item.folder_id) || 0,
      sealed: item.has_sealed === true,
    };
  });
}

export type SignParty = { id: number; name: string; email: string; phone: string; auth_mail: boolean; auth_sms: boolean; otp_exempt: boolean };

export type SignReady = { id: number; title: string; status: string; sender_name: string; parties: SignParty[] };

async function signSend(path: string, body: unknown): Promise<Record<string, unknown>> {
  const headers = new Headers({ "Content-Type": "application/json" });
  const userId = currentUserId();
  if (userId) headers.set("X-User-Id", userId);
  const res = await fetch(`${suiteRoot()}/sign/api/${path}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers,
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 403) throw new Error("La tuile Signature n’est pas ouverte pour ce compte.");
  if (!res.ok || data.ok === false) throw new Error(String(data.message || "Signature ne répond pas."));
  return data;
}

export async function readSignDoc(id: number): Promise<SignReady> {
  const data = await signGet(`documents.php?id=${id}`);
  const doc = (data.document || {}) as { id?: number; title?: string; status?: string; sender_name?: string | null; parties?: SignParty[] };
  return {
    id: Number(doc.id) || id,
    title: doc.title || "Contrat",
    status: doc.status || "draft",
    sender_name: doc.sender_name || "",
    parties: Array.isArray(doc.parties) ? doc.parties : [],
  };
}

export async function sendSignDoc(doc: SignReady, senderName: string, email: string, phone: string): Promise<string[]> {
  const parties = doc.parties.map((party, index) => ({
    id: party.id,
    email: index === 0 ? email : (party.email || ""),
    phone: index === 0 ? phone : (party.phone || ""),
    auth_mail: party.auth_mail || index === 0,
    auth_sms: !!party.auth_sms,
    otp_exempt: !!party.otp_exempt,
  }));
  const data = await signSend("documents.php?action=send", {
    id: doc.id,
    parties,
    sender_name: senderName,
    signer_email: email,
  });
  const mailed = Array.isArray(data.mailed) ? data.mailed.map((item) => String(item)) : [];
  const failed = Array.isArray(data.failed) ? data.failed : [];
  if (mailed.length === 0 && failed.length > 0) {
    const first = failed[0] as { error?: string };
    throw new Error(first.error || "L’invitation n’est pas partie.");
  }
  return mailed;
}
